import { useState, useEffect } from 'react';
import { Filter, X, Plus } from 'lucide-react';

export default function WorklistFilterPanel({ isOpen, onClose, cols = [], filters = [], setFilters, statuses = [], items = [] }) {
    const [drafts, setDrafts] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setDrafts(filters.length > 0 ? [...filters] : [{ id: Date.now(), col: '', op: 'contains', value: '' }]);
        }
    }, [isOpen, filters]);

    const OPS = [
        { v: 'contains',  l: 'Contains' },
        { v: 'eq',        l: 'Equals' },
        { v: 'neq',       l: 'Not equal' },
        { v: 'starts',    l: 'Starts with' },
        { v: 'gt',        l: 'Greater than' },
        { v: 'lt',        l: 'Less than' },
        { v: 'empty',     l: 'Is empty' },
        { v: 'not_empty', l: 'Is not empty' },
    ];

    const addDraft = () => setDrafts(ds => [...ds, { id: Date.now(), col: '', op: 'contains', value: '' }]);
    const updateDraft = (id, key, val) => setDrafts(ds => ds.map(d => d.id === id ? { ...d, [key]: val } : d));
    const removeDraft = (id) => setDrafts(ds => ds.filter(d => d.id !== id));

    const apply = () => {
        const valid = drafts.filter(d => d.col);
        setFilters(valid);
        onClose();
    };

    const clearAll = () => {
        setDrafts([{ id: Date.now(), col: '', op: 'contains', value: '' }]);
        setFilters([]);
    };

    if (!isOpen) return null;

    return (
        <div className="w-[220px] shrink-0 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-extrabold text-gray-700 dark:text-slate-200 uppercase tracking-widest">Filters</span>
                </div>
                <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-800 transition-all">
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Filter conditions */}
            <div className="flex-1 overflow-y-auto px-4 py-3 custom-scrollbar space-y-3">
                {drafts.map((d) => {
                    const needsValue = !['empty', 'not_empty'].includes(d.op);
                    const colDef = cols.find(c => c.key === d.col);
                    const colOpts = colDef?.type === 'dropdown' ? (colDef.options || '').split(',').map(o => o.trim()).filter(Boolean) : [];

                    return (
                        <div key={d.id} className="p-3 rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/30 space-y-2 relative group">
                            {/* Remove button */}
                            {drafts.length > 1 && (
                                <button onClick={() => removeDraft(d.id)} className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-400 hover:text-red-500 shadow-sm opacity-0 group-hover:opacity-100 transition-all">
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            )}

                            {/* Column select */}
                            <select value={d.col} onChange={e => updateDraft(d.id, 'col', e.target.value)}
                                className="w-full text-[11px] font-medium px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-400 transition-all">
                                <option value="">Column…</option>
                                {cols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>

                            {/* Condition select */}
                            <select value={d.op} onChange={e => { updateDraft(d.id, 'op', e.target.value); updateDraft(d.id, 'value', ''); }}
                                className="w-full text-[11px] font-medium px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-400 transition-all">
                                {OPS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>

                            {/* Value input */}
                            {needsValue && (
                                <>
                                    {colOpts.length > 0 ? (
                                        <select value={d.value} onChange={e => updateDraft(d.id, 'value', e.target.value)}
                                            className="w-full text-[11px] font-medium px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-400 transition-all">
                                            <option value="">— any —</option>
                                            {colOpts.map(o => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : colDef?.type === 'date' ? (
                                        <input type="date" value={d.value} onChange={e => updateDraft(d.id, 'value', e.target.value)}
                                            className="w-full text-[11px] font-medium px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-400 transition-all" />
                                    ) : (
                                        <input type="text" placeholder="Value…" value={d.value} onChange={e => updateDraft(d.id, 'value', e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && apply()}
                                            className="w-full text-[11px] font-medium px-2 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-400 transition-all" />
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}

                <button onClick={addDraft} className="w-full py-2 rounded-lg border border-dashed border-gray-200 dark:border-slate-700 text-[11px] font-bold text-gray-400 dark:text-slate-500 hover:border-indigo-300 hover:text-indigo-500 transition-all flex items-center justify-center gap-1.5">
                    <Plus className="w-3 h-3" /> Add
                </button>
            </div>

            {/* Footer */}
            <div className="flex-none px-4 py-3 border-t border-gray-100 dark:border-slate-800 space-y-2">
                <button onClick={apply}
                    className="w-full py-2 rounded-lg text-[11px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm">
                    Apply {drafts.filter(d => d.col).length} Filter{drafts.filter(d => d.col).length !== 1 ? 's' : ''}
                </button>
                <button onClick={clearAll}
                    className="w-full py-1.5 rounded-lg text-[11px] font-semibold text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all">
                    Clear All
                </button>
            </div>
        </div>
    );
}
